import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Clock3, XCircle } from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import {
  activityStatusLabel,
  activityStatusTone,
  formatPlayActivityAt,
  getPlayerResultDisplay,
} from "../../utils/bondPlayActivityHelpers";
import { formatCoinFlipCoin, sideLabel } from "../../utils/coinFlipHelpers";
import { formatBondDiceCoin, faceLabel, oddsForPick, pickLabel } from "../../utils/bondDiceHelpers";
import { formatBondNumbersCoin, numberLabel, pickLabel as numbersPickLabel } from "../../utils/bondNumbersHelpers";

const PlayStatusPill = ({ status }) => {
  return (
    <span className={`bdraw-player-status ${activityStatusTone(status)}`}>
      {activityStatusLabel(status)}
    </span>
  );
};

const FlipGameBlock = ({ game }) => {
  const formatCoin = formatCoinFlipCoin;
  const result = getPlayerResultDisplay(game, formatCoin);

  return (
    <>
      <div className="bdraw-player-meta-row">
        <span className="bdraw-player-meta">{formatPlayActivityAt(game.playedAt) || "—"}</span>
        <PlayStatusPill status={game.status} />
      </div>
      <div className="bdraw-player-metrics bactivity-player-metrics">
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Pick</span>
          <strong>{sideLabel(game.playerSide)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Outcome</span>
          <strong>{sideLabel(game.outcome)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Stake</span>
          <strong>{formatCoin(game.stake)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Max Win</span>
          <strong className="warn">{formatCoin(game.maxWin || Number(game.stake || 0) * 2)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Result</span>
          <strong className={result.tone}>{result.text}</strong>
        </div>
      </div>
    </>
  );
};

const DiceGameBlock = ({ game }) => {
  const formatCoin = formatBondDiceCoin;
  const result = getPlayerResultDisplay(game, formatCoin);
  const pick = game.playerPick ?? (game.playerFace != null ? String(game.playerFace) : null);
  const maxWin = Number(game.maxWin || 0) || Math.floor(Number(game.stake || 0) * oddsForPick(pick));

  return (
    <>
      <div className="bdraw-player-meta-row">
        <span className="bdraw-player-meta">{formatPlayActivityAt(game.playedAt) || "—"}</span>
        <PlayStatusPill status={game.status} />
      </div>
      <div className="bdraw-player-metrics bactivity-player-metrics">
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Pick</span>
          <strong>{pickLabel(pick)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Rolled</span>
          <strong>{faceLabel(game.outcome)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Stake</span>
          <strong>{formatCoin(game.stake)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Max Win</span>
          <strong className="warn">{formatCoin(maxWin)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Result</span>
          <strong className={result.tone}>{result.text}</strong>
        </div>
      </div>
    </>
  );
};

const NumbersGameBlock = ({ game }) => {
  const formatCoin = formatBondNumbersCoin;
  const result = getPlayerResultDisplay(game, formatCoin);
  const maxWin = Number(game.maxWin || game.maxPotentialPayout || 0) || Math.floor(Number(game.stake || 0) * 2);

  return (
    <>
      <div className="bdraw-player-meta-row">
        <span className="bdraw-player-meta">{formatPlayActivityAt(game.playedAt) || "—"}</span>
        <PlayStatusPill status={game.status} />
      </div>
      <div className="bdraw-player-metrics bactivity-player-metrics">
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Pick</span>
          <strong>{numbersPickLabel(game.playerPick)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Ref → Reveal</span>
          <strong>
            {numberLabel(game.referenceNumber)} → {numberLabel(game.outcome)}
          </strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Stake</span>
          <strong>{formatCoin(game.stake)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Max Win</span>
          <strong className="warn">{formatCoin(maxWin)}</strong>
        </div>
        <div className="bdraw-player-metric">
          <span className="bdraw-player-metric-label">Result</span>
          <strong className={result.tone}>{result.text}</strong>
        </div>
      </div>
    </>
  );
};

const BondPlayPlayerCard = ({ player, game = "flip", isCurrentUser = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { recent, recentGames = [] } = player;
  const hasRecent = recent && String(recent.status || "").toLowerCase() !== "pending";
  const avatarUrl = generateAvatarUrl(player.name || "Player");
  const GameBlock =
    game === "dice" ? DiceGameBlock : game === "numbers" ? NumbersGameBlock : FlipGameBlock;
  const playedCount = Number(player.won || 0) + Number(player.lost || 0);
  const historyGames = recentGames.slice(hasRecent ? 1 : 0, 5);

  return (
    <li className={`bdraw-player-card${expanded ? " expanded" : ""}${isCurrentUser ? " mine" : ""}`}>
      <button
        type="button"
        className="bdraw-player-card-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <img className="bdraw-player-avatar" src={avatarUrl} alt="" />
        <span className="bdraw-player-head-main">
          <span className="bdraw-player-name-row">
            <strong>{player.name || "Anonymous"}</strong>
            {isCurrentUser ? <span className="bdraw-player-you">YOU</span> : null}
          </span>
          <span className="bactivity-card-subtitle">
            {playedCount > 0 ? `${playedCount} settled ${playedCount === 1 ? "game" : "games"}` : "Waiting for first result"}
          </span>
        </span>
        <span className="bdraw-panel-row-stats">
          <span className="bdraw-stat won" title="Won">
            <CheckCircle2 size={11} strokeWidth={2.5} />
            {player.won || 0}
          </span>
          <span className="bdraw-stat lost" title="Lost">
            <XCircle size={11} strokeWidth={2.5} />
            {player.lost || 0}
          </span>
          <span className="bdraw-stat pending" title="Pending">
            <Clock3 size={11} strokeWidth={2.5} />
            {player.pending || 0}
          </span>
        </span>
        <span className="bdraw-player-chevron" aria-hidden>
          {expanded ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
        </span>
      </button>

      {expanded ? (
        <div className="bdraw-player-card-body">
          {hasRecent ? (
            <>
              <GameBlock game={recent} />
              {historyGames.length > 0 ? (
                <div className="bdraw-player-history">
                  <span className="bdraw-player-history-title">Recent</span>
                  {historyGames.map((gameRow, idx) => (
                    <div key={`rg-${idx}`} className="bdraw-player-history-card">
                      <GameBlock game={gameRow} />
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="bdraw-player-empty-detail">No completed game yet</p>
          )}
        </div>
      ) : null}
    </li>
  );
};

export default BondPlayPlayerCard;
